import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../Component/shared/datePicker/Index';
import SelectDropdown from '../../Component/shared/dropdown/selectDropdown/Index';
import './AddAppointmentModal.css';
import { GetDoctors, AddAppointment, getVisitTypeBySpecialtyAndDoctor } from './Service';
import { getSessionData } from '../../helpers/Util';
import toast from "react-hot-toast";
import Modal from "react-bootstrap/Modal";


/* ── Shared canvas helpers ── */
const getDPR = () => window.devicePixelRatio || 1;
const getXY = (e, canvas) => {
  const r = canvas.getBoundingClientRect();
  const src = e.touches ? e.touches[0] : e;
  return { x: src.clientX - r.left, y: src.clientY - r.top };
};

/* ── Custom dropdown for time parts ── */
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function TimeSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div ref={ref} className="ts-wrap">
      <button type="button" className="ts-trigger" onClick={() => setOpen(o => !o)}>
        {value}
      </button>
      {open && (
        <div className="ts-panel">
          {options.map(opt => (
            <div
              key={opt}
              className={`ts-option${opt === value ? ' ts-option--active' : ''}`}
              onMouseDown={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AddAppointmentModal({ isOpen, onClose, patientName = '', onCheckIn }) {
  const [apptDate, setApptDate] = useState('');
  const [timeFrom, setTimeFrom] = useState({ hour: '08', minute: '00', ampm: 'AM' });
  const [timeTo, setTimeTo] = useState({ hour: '08', minute: '30', ampm: 'AM' });
  const [specialty, setSpecialty] = useState('');
  const [doctor, setDoctor] = useState('');
  const [errors, setErrors] = useState({});
  const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState(false);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [doctorData, setDoctorData] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setApptDate('');
      setTimeFrom({ hour: '08', minute: '00', ampm: 'AM' });
      setTimeTo({ hour: '08', minute: '30', ampm: 'AM' });
      setSpecialty('');
      setDoctor('');
      setErrors({});
      return;
    }
    handleGetDoctors();
  }, [isOpen]); // eslint-disable-line


  const handleGetDoctors = async () => {
    try {
      const sessionData = getSessionData('tokenData', {});
      const providerIds = getSessionData('providerIds', '');
      const tokenData = getSessionData('tokenData', '');
      const finalProviderIds = providerIds || tokenData?.providerId || '';
      const response = await GetDoctors(finalProviderIds, sessionData?.companyId);
      if (response?.responseCode === 200) {
        setDoctorData(response?.data || []);
      }
      else {
        toast.error(response?.responseMessage || 'Failed to fetch doctors');
        console.error('Error fetching doctors:', response?.responseMessage);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      toast.error('Failed to fetch doctors');
      return [];
    }
  };

  const getVisitType = async (caseid, specialtyIds) => {
    try {
      const res = await getVisitTypeBySpecialtyAndDoctor(caseid, specialtyIds);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const result = await res.json();
      if (result.data) {
        const visitType = result.data;
        return visitType;
      }
    } catch (error) {
      // toast.error("Failed to fetch visit type");
      console.error(error);
    }
  }

  const handleAddAppointment = async (btForceCreateVisit) => {
    const newErrors = {};
    if (!apptDate) newErrors.apptDate = "Appointment Date is required";
    if (!doctor) newErrors.doctor = "Doctor is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let finalSpecialty = specialty;
    if (!finalSpecialty && doctor) {
      const selectedDoc = doctorData?.find(d => d.doctorId === doctor);
      if (selectedDoc) {
        finalSpecialty = selectedDoc.procedureGroupId;
      }
    }

    setIsLoading(true);
    try {
      let requestBody = await createVisitProfile(btForceCreateVisit, finalSpecialty);

      const result = await AddAppointment(requestBody)
      if (result?.errors === null) {
        if (result?.data?.[0].error) {
          const errorMsg = result?.data?.[0].error.replace("The patient already has", "You already have");
          setAlreadyExistsMessage(errorMsg);
          setShowAlreadyExistsModal(true);
        } else {
          const formattedDate = new Date(requestBody?.dtEventDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
          setSuccessMessage(<span>Appointment created successfully for <strong style={{ color: '#2a9d8f' }}>{formattedDate}</strong></span>);
          setShowSuccessModal(true);
        }
        let eventId = result?.data?.[0]?.key;

      } else {
        toast.error('Failed to create the appointment');
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Something went wrong while creating the appointment.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime12h = (t) => `${t.hour}:${t.minute} ${t.ampm}`;

  const createVisitProfile = async (btForceCreateVisit, finalSpecialty) => {
    const tokenData = getSessionData('tokenData', {});
    const visitType = await getVisitType(tokenData?.caseId, finalSpecialty);
    const [year, month, day] = apptDate.split("-");
    const formattedDate = `${month}/${day}/${year}`;

    return {
      addedByUser: "",
      addedByUserId: tokenData?.userId || '',
      address: "",
      age: "",
      anesDoctorId: "",
      anesType: 0,
      appointmentTime: formatTime12h(timeFrom),
      appointmentTimeEnd: formatTime12h(timeTo),
      arrivalTime: "",
      cancellationComment: "",
      caseId: tokenData?.caseId,
      caseNo: "",
      caseType: "",
      colorCode: "",
      companyId: tokenData?.companyId || '',
      departureTime: "",
      doa: "",
      dob: "",
      doctorId: doctor,
      doctorName: '',
      dtEventDate: formattedDate,
      end: "",
      equipment: "",
      eventDate: "",
      eventType: visitType,
      eventTypeEnd: "",
      eventTypeId: "",
      gender: "",
      id: 0,
      insuranceVerificationStatus: "",
      isAnesVisit: "",
      isBilled: false,
      isFinalize: false,
      noOfDays: 0,
      patientId: tokenData?.patientId || '',
      patientName: '',
      patientNotes: "",
      patientPhone: "",
      pickUpTime: "",
      preID: 0,
      procedureGroup: "",
      procedureGroupID: finalSpecialty,
      reffCompanyId: "",
      reffDoctorId: "",
      reffOfficeId: "",
      roomId: "",
      start: "",
      status: '0',
      testFacility: "",
      title: "",
      transportId: "",
      value: "",
      visitNotes: '',
      visitStatus: "",
      visitTypeID: "",
      repeatVisitOption: null,
      role: '',
      procedureGroupCodeList: '',
      btForceCreateVisit: btForceCreateVisit ? btForceCreateVisit : false
    }
  };

  const specialtyOptions = useMemo(() => {
    if (!doctorData || !Array.isArray(doctorData)) return [];
    const uniqueMap = new Map();
    doctorData.forEach(item => {
      if (item.procedureGroupId && !uniqueMap.has(item.procedureGroupId)) {
        uniqueMap.set(item.procedureGroupId, { value: item.procedureGroupId, label: item.procedureGroup });
      }
    });
    return Array.from(uniqueMap.values());
  }, [doctorData]);

  const doctorOptions = useMemo(() => {
    if (!doctorData || !Array.isArray(doctorData)) return [];

    let filteredData = doctorData;
    if (specialty !== undefined && specialty !== null && specialty !== '') {
      filteredData = doctorData.filter(item => item.procedureGroupId == specialty);
    }

    const uniqueMap = new Map();
    filteredData.forEach(item => {
      if (item.doctorId && !uniqueMap.has(item.doctorId)) {
        uniqueMap.set(item.doctorId, { value: item.doctorId, label: item.doctorName });
      }
    });
    return Array.from(uniqueMap.values());
  }, [doctorData, specialty]);



  if (!isOpen) return null;

  return createPortal(
    <div className="cim-overlay">
      <div className="cim-modal" style={{ position: 'relative' }}>
        {isLoading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px' }}>
            <div style={{ width: 40, height: 40, border: '4px solid #e5ddd3', borderTopColor: '#2a9d8f', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          </div>
        )}

        {/* Header */}
        <div className="cim-header">
          <div className="cim-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="cim-header-info">
            <h2 className="cim-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              New Appointment
              {patientName && patientName.trim() && (
                <span className="cim-badge" style={{ margin: 0, fontSize: '13px' }}>
                  {patientName.trim()}
                </span>
              )}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="cim-close-btn"
            title="Close"
            style={{ border: 'none', background: 'transparent', marginLeft: 'auto' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="cim-body">
          {/* Row 1: Date + Time From + Time To */}
          <div className="cim-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            {/* Date */}
            <div className="cim-field">
              <span className="cim-field-label">Appointment Date <span style={{ color: '#ef4444' }}>*</span></span>
              <DatePicker
                value={apptDate}
                onChange={(val) => { setApptDate(val); setErrors(prev => ({ ...prev, apptDate: null })); }}
                maxDate="2099-12-31"
                hasError={!!errors.apptDate}
              />
              {errors.apptDate && <span style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>{errors.apptDate}</span>}
            </div>

            {[['Time From', timeFrom, setTimeFrom], ['Time To', timeTo, setTimeTo]].map(([label, val, setter]) => (
              <div key={label} className="cim-field">
                <span className="cim-field-label">{label} <span style={{ color: '#ef4444' }}>*</span></span>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', height: '38px' }}>
                  <TimeSelect
                    value={val.hour}
                    options={HOURS}
                    onChange={(h) => setter(prev => ({ ...prev, hour: h }))}
                  />
                  <span style={{ fontWeight: 700, color: '#7d6e61', fontSize: '13px', flexShrink: 0 }}>:</span>
                  <TimeSelect
                    value={val.minute}
                    options={MINUTES}
                    onChange={(m) => setter(prev => ({ ...prev, minute: m }))}
                  />
                  <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5ddd3', flexShrink: 0, height: '100%' }}>
                    {['AM', 'PM'].map(period => (
                      <button
                        key={period}
                        type="button"
                        onClick={() => setter(prev => ({ ...prev, ampm: period }))}
                        style={{
                          padding: '0 9px',
                          border: 'none',
                          fontSize: '10px',
                          fontWeight: 700,
                          fontFamily: 'Montserrat, sans-serif',
                          letterSpacing: '0.04em',
                          cursor: 'pointer',
                          transition: 'background 0.15s, color 0.15s',
                          background: val.ampm === period ? '#2a9d8f' : '#f0e9de',
                          color: val.ampm === period ? '#fff' : '#7d6e61',
                        }}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Row 2: Specialty + Doctor */}
          <div className="cim-row">
            <SelectDropdown
              label={<>Specialty</>}
              value={specialty}
              options={specialtyOptions}
              onChange={(val) => {
                setSpecialty(val);
                setDoctor('');
                setErrors(prev => ({ ...prev, specialty: null, doctor: null }));
              }}
              error={errors.specialty}
            />
            <SelectDropdown
              label={<>Doctor <span style={{ color: '#ef4444' }}>*</span></>}
              value={doctor}
              options={doctorOptions}
              onChange={(val) => { setDoctor(val); setErrors(prev => ({ ...prev, doctor: null })); }}
              error={errors.doctor}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="cim-footer">
          <button className="cim-btn cim-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="cim-btn cim-btn-submit" onClick={() => handleAddAppointment(false)}>
            Save
            {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg> */}
          </button>
        </div>

      </div>

      {showAlreadyExistsModal && createPortal(
        <div className="cim-overlay">
          <div className="cim-modal" style={{ maxWidth: '420px' }}>
            {/* Header */}
            <div className="cim-header" style={{ borderBottom: '1px solid #fde68a', background: '#fffbeb' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(234,179,8,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#b45309' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="cim-header-info">
                <h2 className="cim-title" style={{ color: '#92400e' }}>Visit Already Exists !</h2>
              </div>
            </div>
            {/* Body */}
            <div className="cim-body">
              <p style={{ fontSize: '14px', color: '#3d332a', margin: 0, lineHeight: 1.6 }}>{alreadyExistsMessage}</p>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#1a1410', margin: '8px 0 0' }}>Do you still want to add another visit?</p>
            </div>
            {/* Footer */}
            <div className="cim-footer">
              <button
                className="cim-btn cim-btn-cancel"
                onClick={() => { setShowAlreadyExistsModal(false) }}
              >
                No, Cancel
              </button>
              <button
                className="cim-btn cim-btn-submit"
                onClick={() => { setShowAlreadyExistsModal(false); handleAddAppointment(true); }}
              >
                Yes, Add Anyway
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showSuccessModal && createPortal(
        <div className="cim-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowSuccessModal(false); onClose?.(); } }}>
          <div className="cim-modal" style={{ maxWidth: '400px' }}>
            {/* Header */}
            <div className="cim-header" style={{ borderBottom: '1px solid #e5ddd3', background: '#fdfbf8' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(42,157,143,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#2a9d8f' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div className="cim-header-info">
                <h2 className="cim-title" style={{ color: '#1a1410' }}>Success</h2>
                <p className="cim-subtitle">Appointment Created</p>
              </div>
            </div>
            {/* Body */}
            <div className="cim-body">
              <p style={{ fontSize: '14px', color: '#3d332a', margin: 0, lineHeight: 1.6 }}>{successMessage}</p>
            </div>
            {/* Footer */}
            <div className="cim-footer" style={{ justifyContent: 'flex-end' }}>
              <button
                className="cim-btn cim-btn-submit"
                onClick={() => { setShowSuccessModal(false); onClose?.(); }}
              >
                Okay
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
}
